import React, { useEffect, useState, FC, useRef } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import * as types from "../gql/graphql";

import { Dialog, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { Calendar } from 'primereact/calendar';
import "primereact/resources/themes/md-light-indigo/theme.css";
import MDButtonRoot from './MDButton/MDButtonRoot';
import MDTypographyRoot from './MDTypography/MDTypographyRoot';
import { CalendarTree } from '../utils/CalendarUtils';
import { SchoolTime } from '../utils/SchoolTime';
import { useQueryAll } from '../apolloClient';


interface AddEventDialogProps {
    regId: number;
    classId: number;
    studentId: number;
    onSave: () => void;
    onClose: () => void;
}

// Get events which are not filled to capacity
const getAvailableEventsForClassQuery = gql(`
query GetEventsForClass($classId: BigInt!, $after: Cursor) {
    get_available_events_for_class(p_id: $classId, after: $after)
      {
        pageInfo { endCursor startCursor hasNextPage }       
        edges {node{date status id}}
      }
    }
`);

const getAllEventsForClassQuery = gql(`
query GetAvailableEventsForClass($classId: BigInt!, $after: Cursor) {
    fs_eventsCollection(filter: {class: {eq: $classId}}, after: $after) {
        pageInfo { endCursor startCursor hasNextPage }   
        edges {node{date status id}}
    }
}
`);

const getAllSignupsForStudentClassQuery = gql(`
query GetAllSignupsForStudentClass($studentId: BigInt!, $classId: BigInt!, $after: Cursor) {
    get_student_events_for_class(p_class: $classId, p_student: $studentId, after: $after) {
      pageInfo { endCursor startCursor hasNextPage }  
      edges {
        node {
          fs_events {
            date
            id
          }
          status
        }
      }
    }
  }
`);

const addSignupQuery = gql(`
mutation AddSignup($eventId: BigInt!, $regId: BigInt!, $addToWL: Boolean!) {
    add_signup(
      p_event_id: $eventId, 
      p_registration_id: $regId,
      p_add_to_wl: $addToWL,
      p_staff: true
    ) 
}
`);

// Add signup field to the event
interface StudentEvent extends types.Fs_Events {
  signUpStatus: number; // 0 - full, 1 - available for signup, 2 - signed up, 3 - in wait list, 4 - attended
}

const AddEventDialog: FC<AddEventDialogProps> = ({ regId, studentId, classId, onSave, onClose }) => {
    const { data: AvailableEventsData, loading: AvailableEventsLoading } = useQueryAll<types.Fs_EventsEdge>({query: getAvailableEventsForClassQuery,  variables: { classId: classId } });
    const { data: AllEventsData, loading: AllEventsLoading } = useQueryAll<types.Fs_EventsEdge>({query: getAllEventsForClassQuery,  variables: { classId: classId }});
    const { data: SignUpsData, loading: SignUpsLoading } = useQueryAll<types.Fs_SignupEdge>({query: getAllSignupsForStudentClassQuery,  variables: { classId: classId, studentId: studentId }});
    const [addEventMutation, { loading: addEventLoading, error: addEventError, data: addEventData }] = useMutation(addSignupQuery, {
        refetchQueries: [
        ],
    });

    const [dates, setDates] = useState<CalendarTree<StudentEvent>>(null);

    if (addEventData?.add_signup == 0) onSave();

    useEffect(
        () => {
            console.log("Before check" + AvailableEventsLoading + ", " + AllEventsLoading + ", " + SignUpsLoading);
            if (!AvailableEventsLoading && !AllEventsLoading && !SignUpsLoading) {
                console.log("All lists loaded:" + AllEventsData?.length + ", " + AvailableEventsData?.length + ", " + SignUpsData?.length);
                const nt = new CalendarTree<StudentEvent>();
                AllEventsData?.forEach((e) => {
                    nt.addEvent({ ...e.node, signUpStatus: 0 });
                })
                // Second call overwrites events retrieved before
                AvailableEventsData?.forEach((e) => {
                    nt.addEvent({ ...e.node, signUpStatus: 1 });
                })
                SignUpsData?.forEach((e) => {
                    const event_date = SchoolTime.fromISO(e.node.fs_events.date);
                    const event = nt.getEvent(event_date.getYear(), event_date.getMonth(), event_date.getDay(), e.node.fs_events.id);
                    // scheduled - 0, attended - 1, waitlist - 2
                    (e.node.status == 0) && (event.signUpStatus = 2);
                    (e.node.status == 2) && (event.signUpStatus = 3);
                    (e.node.status == 1) && (event.signUpStatus = 4);
                })
                setDates(nt);
            }
        },
        [AvailableEventsData, AllEventsData, SignUpsData]
    );

    const handleSignUp = () => {
        //        deleteRegistrationMutation({ variables: { id: isConfirmRegistrationDeletionOpen  } });
        onClose();
    }

    const handleDaySelection = (day_selection) => {
        const event_array = dates.getEvents(day_selection.value.getFullYear(), day_selection.value.getMonth() + 1, day_selection.value.getDate()); // Calendar returns JS Date, adjusting month
        // TODO: add dialog to select from multiple events during the same day. For now don't handle this case.
        // TODO: handle move from WL to signed
        const event = event_array[0];
        if (!event) return;
        if (event.signUpStatus == 1) {
            // Function takes care of canceling corresponding WL
            addEventMutation({ variables: { regId: regId, eventId: event.id, addToWL: false } });
        } else if (event.signUpStatus == 0) {
            // Not enough space, increase event attendance by 1. If already in WL for that event, remove from WL.
            addEventMutation({ variables: { regId: regId, eventId: event.id, addToWL: false } });
        }

        //onClose();
    }


    // Your modal content and form for editing the row data

    // Is it needed?
    const handleEntered = () => {
        // The `autoFocus` is not used because, if used, the same Enter that saves
        // the cell triggers "No". Instead, we manually focus the "No" button once
        // the dialog is fully open.
        // noButtonRef.current?.focus();
    };

    //const noButtonRef = React.useRef<HTMLButtonElement>(null);

    const dateTemplate = (date) => {
        const dl = dates?.getEvents(date.year, date.month + 1, date.day); // JS date - month starts from 0
        if (dl?.length > 0) {
            var status = '';
            if (dl.length > 1) status = '?';
            else if (dl[0].signUpStatus == 0) status = 'W';
            else if (dl[0].signUpStatus == 1) status = 'A';
            else if (dl[0].signUpStatus == 2) status = 'S'; // Signed
            else if (dl[0].signUpStatus == 3) status = '?'; // In WL
            else if (dl[0].signUpStatus == 4) status = 'C'; // Completed

            return (
                <div><strong style={{ fontWeight: 'bolder' }}>{date.day}</strong> <sup className="num-circle">{status}</sup></div>
            );
        }

        return date.day;
    }

    return (
        <>
            {/* ================== Add sign-up dates =================== */}
            <Dialog onClose={onClose} open={true}>
                <MDTypographyRoot variant="h5" px={1} py={1}>
                    Select the date
                    <CloseIcon onClick={onClose}
                        sx={{ position: "absolute", top: "10px", right: "10px" }} />
                </MDTypographyRoot>
                <DialogContent dividers>
                    <DialogContentText id="registration-delete-alert-content">
                        No dates available
                    </DialogContentText>

                    <Calendar viewDate={SchoolTime.now().toJSDate()} inline onChange={handleDaySelection} dateTemplate={dateTemplate} />

                </DialogContent>
                <DialogActions>
                    <MDButtonRoot
                        size="medium"
                        onClick={onClose}
                        autoFocus
                        ownerState={{
                            textTransform: "none",
                            variant: "gradient",
                            color: "warning",
                        }}>
                        Cancel </MDButtonRoot>
                </DialogActions>
            </Dialog>
        </>
    );
};


export default AddEventDialog;
